import axios from 'axios';
import { http } from './http';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const CONCURRENCY = 5;

// Global state to track active upload for aborting
let activeUpload = {
    controller: null,
    uploadId: null,
    key: null,
};

/**
 * Multi-part file upload with S3 direct upload and concurrency control
 * @param {File} file - The file to upload
 * @param {Object} options - { onProgress, onIdGenerated }
 * @returns {Promise}
 */
export const uploadFileInChunks = async (file, { onProgress, onIdGenerated } = {}) => {
    const totalParts = Math.ceil(file.size / CHUNK_SIZE);

    // Reset/Initialize the global controller for this single active upload
    if (activeUpload.controller) {
        activeUpload.controller.abort();
    }
    activeUpload.controller = new AbortController();
    const signal = activeUpload.controller.signal;

    let uploadId, key;

    try {
        // 1. Start upload
        const startRes = await http.post('/frontend-document-files/start-upload', {
            fileName: file.name,
            fileType: file.type,
            mimeType: file.type,
            fileSize: file.size,
        }, { signal });

        uploadId = startRes.data?.data?.uploadId;
        key = startRes.data?.data?.key;

        activeUpload.uploadId = uploadId;
        activeUpload.key = key;

        if (onIdGenerated) {
            onIdGenerated({ uploadId, key });
        }

        // 2. Get Signed URLs for all parts
        const parts = Array.from({ length: totalParts }, (_, i) => i + 1);
        const urlRes = await http.post('/frontend-document-files/generate-upload-urls', {
            uploadId,
            key,
            parts,
        }, { signal });

        const urls = urlRes.data?.data;
        const uploadedParts = [];
        let completedChunks = 0;

        // 3. Upload parts with concurrency control
        for (let i = 0; i < urls.length; i += CONCURRENCY) {
            const batch = urls.slice(i, i + CONCURRENCY);

            // 1. Upload in parallel
            const results = await Promise.all(
                batch.map(async (partInfo) => {
                    const start = (partInfo.partNumber - 1) * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, file.size);

                    const res = await axios.put(
                        partInfo.url,
                        file.slice(start, end),
                        {
                            headers: { 'Content-Type': file.type },
                            signal
                        }
                    );

                    const etag = (res.headers.etag || res.headers.ETag).replace(/"/g, '');

                    completedChunks++;
                    if (onProgress) {
                        onProgress(Math.round((completedChunks / totalParts) * 100));
                    }

                    return {
                        PartNumber: partInfo.partNumber,
                        ETag: etag,
                    };
                })
            );

            // 2. Send batch in ONE API call
            await http.post(
                `/frontend-document-files/add-uploaded-parts/${uploadId}`,
                {
                    parts: results.map((p) => ({
                        partNumber: p.PartNumber,
                        etag: p.ETag,
                    })),
                },
                { signal }
            );

            uploadedParts.push(...results);
        }

        // 4. Complete upload
        await http.post('/frontend-document-files/complete-upload', {
            uploadId,
            key,
            parts: uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber),
        }, { signal });

        // Cleanup global state on success
        activeUpload = { controller: null, uploadId: null, key: null };
        return { key, uploadId };

    } catch (error) {
        console.error('Multi-part upload failed:', error);
        await abortUpload();
        throw error;
    }
};

/**
 * Abort the active multi-part upload and cancel all pending HTTP requests
 */
export const abortUpload = async () => {
    try {
        // 1. Abort all active HTTP requests via signal
        if (activeUpload.controller) {
            activeUpload.controller.abort();
        }

        // 2. Call backend to cleanup S3 if we have IDs
        if (activeUpload.uploadId && activeUpload.key) {
            await http.post('/frontend-document-files/abort-upload', {
                uploadId: activeUpload.uploadId,
                key: activeUpload.key,
            });
        }
    } catch (err) {
        console.error('Failed to abort upload:', err);
    } finally {
        activeUpload = { controller: null, uploadId: null, key: null };
    }
};
